/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Soonki Min (skmin@rsupport.com)
 *
 * @description
 *
 * @created at Tue Mar 28 2023
 **/

import { css, Theme } from "@emotion/react";
import styled from "@emotion/styled";

import { Column, Row } from "../../../Layouts";
import { TypographyCSS } from "../../../Typography";

const commonInputCSS = (error: boolean, theme: Theme, size?: 32 | 36 | 40 | 44) => css`
	border: 1px solid ${error ? theme.colors.error_60_100 : theme.colors.neutral_30_100};
	border-radius: 8px;
	width: 100%;
	resize: none;
	${TypographyCSS("Body3")}
	height: ${size}px;
	background-color: ${theme.colors.neutral_00_100};

	input[type="number"],
	&[type="number"] {
		margin: 0;
		&::-webkit-outer-spin-button,
		::-webkit-inner-spin-button {
			-webkit-appearance: none;
		}
	}

	&:hover {
		border: 1px solid ${error ? theme.colors.error_60_100 : theme.colors.neutral_80_50};
	}

	&:focus {
		outline: none;
		border: 1px solid ${error ? theme.colors.error_60_100 : theme.colors.neutral_80_100};
	}

	&:disabled {
		background: ${theme.colors.neutral_20_100};
		color: ${theme.colors.neutral_80_32};
		border: 1px solid ${theme.colors.neutral_30_100};
	}

	&::placeholder {
		color: ${theme.colors.neutral_80_50};
	}
`;

export const Wrapper = styled(Column.label)`
	position: relative;
	width: 100%;
`;

export const Input = styled(Row)<{ error: boolean; size: 32 | 36 | 40 | 44 }>`
	padding: 0px 12px;
	${({ error, size, theme }) => commonInputCSS(error, theme, size)}
`;

export const IconInput = styled(Row)<{ error: boolean; size: 32 | 36 | 40 | 44 }>`
	padding: 0 12px;
	${({ error, theme, size }) => commonInputCSS(error, theme, size)}
`;

export const IconWrapper = styled(Row)`
	position: relative;
	width: 100%;
`;

export const LeftIconWrapper = styled(Row)<{ size?: number | string; disabled?: boolean; fill?: string }>`
	position: absolute;
	top: 50%;
	transform: translateY(-50%);
	left: ${({ size }) => (size === 24 ? "4px" : "12px")};
	svg {
		fill: ${({ theme, disabled, fill }) =>
			disabled ? theme.colors.neutral_80_32 : fill ? fill : theme.colors.neutral_80_100};
	}
`;

export const RightIconWrapper = styled(Row)<{ disabled?: boolean; fill?: string }>`
	position: absolute;
	top: 50%;
	transform: translateY(-50%);
	right: 4px;

	&:hover {
		background-color: ${({ theme, disabled }) => !disabled && theme.colors.secondary_80_8};
		border-radius: 50%;
	}

	svg {
		fill: ${({ theme, disabled, fill }) =>
			disabled ? theme.colors.neutral_80_32 : fill ? fill : theme.colors.neutral_80_100};
	}
`;

export const HelpText = styled.p<{ error: boolean; success: boolean; disabled?: boolean }>`
	${TypographyCSS("Body5")}
	color: ${({ theme, error, disabled, success }) =>
		disabled
			? theme.colors.neutral_60_100
			: error
				? theme.colors.error_60_100
				: success
					? theme.colors.accent_60_100
					: theme.colors.neutral_60_100};
`;

export const LabelText = styled.p`
	${TypographyCSS("Body4")}
	color: ${({ theme }) => theme.colors.neutral_60_100};
	display: flex;
	align-items: center;
`;

export const LabelWrapper = styled(Row)`
	width: 100%;
`;

export const TagWrapper = styled(Row)<{ error: boolean; maxHeight: UnitString; minHeight: UnitString }>`
	padding: 3px 12px;
	overflow: auto;
	position: relative;
	flex-wrap: wrap;
	max-height: ${({ maxHeight }) => maxHeight};
	min-height: ${({ minHeight }) => minHeight};
	&::-webkit-scrollbar {
		display: none;
	}

	${({ error, theme }) => commonInputCSS(error, theme)}
	input {
		flex-grow: 1;
		border: none;
		outline: none;
		height: 32px;
		${TypographyCSS("Body3")}
	}
`;

export const DropdownWrapper = styled(Column)`
	position: absolute;
	left: 0;
	top: calc(100% + 3px);
	z-index: 100;
	width: 100%;
	padding: 6px 2px 6px 6px;
	border-radius: 6px;
	border: 1px solid ${({ theme }) => theme.colors.neutral_30_100};
	background-color: ${({ theme }) => theme.colors.neutral_00_100};
`;
